<script setup lang="ts">
import MultiSelect from 'primevue/multiselect';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';

import Button from 'lib/components/button/Button.vue';
import { OptionValue } from 'lib/components/dropdown/Dropdown.vue.d';
import FieldWrapper from 'lib/components/fieldwrapper/FieldWrapper.vue';
import Icon from 'lib/components/icon/Icon.vue';
import InputGroup from 'lib/components/inputgroup/InputGroup.vue';
import type {
  MultiSelectEmits,
  MultiSelectProps,
  MultiSelectSlots,
} from 'lib/components/multiselect/MultiSelect.vue.d';
import ValidatorMessage from 'lib/components/validatormessage/ValidatorMessage.vue';
import eventBus from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import filterOptions from 'lib/utils/filterOptions.util';
import { formatVowelSoundLabel } from 'lib/utils/textFormatter.util';

import { FieldValidation } from '../form/Form.vue.d';

const props = withDefaults(defineProps<MultiSelectProps>(), {
  display: 'chip',
  filter: true,
  showOptionalText: undefined,
  hideHeaderCheckbox: false,
});

const emit = defineEmits<MultiSelectEmits>();
const slots = defineSlots<MultiSelectSlots>();

onMounted(() => {
  setValidator();
  eventBus.on('multi-select:hide-overlay', hideOverlay);
});

onUnmounted(() => {
  eventBus.off('multi-select:hide-overlay');
});

const { locale } = useWangsVueConfig('MultiSelect');
const Preset = inject<Record<string, any>>('preset', {}).multiselect;

const isShowOverlay = shallowRef<boolean>(false);
const multiselect = ref<MultiSelect>();

const field = reactive<FieldValidation<OptionValue[] | undefined>>({
  value: props.initialValue ? props.initialValue : props.modelValue,
});

const visibleOptions = computed(() => filterOptions(props.options));

const emptyErrorMessage = computed(() => {
  return locale.emptySelectionErrorMessage
    .replace('{label}', props.label)
    .replace(
      '{formattedLabel}',
      formatVowelSoundLabel(props.label)?.toLowerCase(),
    );
});

const multiSelectPlaceholder = computed(() => {
  return props.loading
    ? locale.loadingPlaceholder
    : (props.placeholder ??
        locale.inputPlaceholder
          ?.replace('{label}', props.label ?? '')
          .replace('{lowercaseLabel}', (props.label ?? '').toLowerCase()));
});

const setValidator = (): void => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'multiSelect', (value: string | number) => {
        const hasValue = Array.isArray(value) && value.length;

        if (!hasValue && props.mandatory) {
          if (
            typeof props.validatorMessage === 'object' &&
            'empty' in props.validatorMessage
          ) {
            return props.validatorMessage.empty;
          }

          return (
            (props.validatorMessage as string | boolean) ??
            emptyErrorMessage.value
          );
        }

        return true;
      }),
    );

    if (props.initialValue?.length) field.value = props.initialValue;
  }
};

/**
 * Method to show the overlay using button trigger.
 *
 * Used when the input should be hidden and controls the multi select overlay from parent component.
 */
const showOverlay = (): void => {
  if (multiselect.value) multiselect.value.show();
};

/**
 * Method to show the overlay using button trigger.
 *
 * Used when the input should be hidden and controls the multi select overlay from parent component.
 */
const hideOverlay = (): void => {
  if (multiselect.value) multiselect.value.hide();
};

const onUpdateValue = (value?: OptionValue[]): void => {
  /**
   * Prevent model value has empty array, it will lead unexpected result of API request.
   */
  const modelValue = value?.length ? value : undefined;
  emit('update:modelValue', modelValue);
};

watch(
  () => field.value,
  () => {
    onUpdateValue(field.value);
  },
);

watch(
  () => props.modelValue,
  (value) => {
    field.value = value;
  },
);

watch(
  () => props.initialValue,
  (value) => {
    field.value = value;
  },
  { once: true },
);

defineExpose({
  isShowOverlay,
  showOverlay,
  hideOverlay,
});
</script>

<template>
  <FieldWrapper
    :class="{
      '!w-max': controls || slots.trigger,
    }"
    :info="fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
    :show-optional-text="showOptionalText"
  >
    <InputGroup
      :class="{ 'rounded-none !ring-0 !w-max': controls || slots.trigger }"
      :disabled="props.disabled"
      :invalid="!!field.errorMessage"
    >
      <MultiSelect
        v-bind="props"
        ref="multiselect"
        v-model="field.value"
        :aria-describedby="props.label + 'error'"
        :class="[
          {
            'invisible !w-0': controls || slots.trigger,
          },
        ]"
        :display="props.display"
        :filter-placeholder="
          props.filterPlaceholder || locale.filterPlaceholder
        "
        :max-selected-labels="props.maxSelectedLabels"
        :options="visibleOptions"
        :placeholder="multiSelectPlaceholder"
        :pt="{
          headerCheckbox: {
            ...Preset.headerCheckbox,
            root: {
              ...Preset.headerCheckbox.root,
              class: [
                ...Preset.headerCheckbox.root.class,
                { hidden: hideHeaderCheckbox },
              ],
            },
          },
        }"
        :selected-items-label="props.selectedItemsLabel"
        :show-toggle-all="props.filter"
        :virtual-scroller-options="
          (options?.length ?? 0) > 10 ? { itemSize: 32 } : undefined
        "
        @hide="(isShowOverlay = false), $emit('hide')"
        @show="$emit('show'), (isShowOverlay = true)"
      >
        <template #value="{ placeholder }" v-if="loading">
          <span v-bind="Preset.loadingplaceholder">{{ placeholder }}</span>
        </template>

        <template #dropdownicon>
          <Icon v-bind="Preset.dropdownicon({ state: { isShowOverlay } })" />
        </template>

        <template #headercheckboxicon>
          <Icon icon="check" />
        </template>

        <template #itemcheckboxicon>
          <Icon icon="check" />
        </template>

        <template #filtericon>
          <Icon v-bind="Preset.filtericon" />
        </template>

        <template #removetokenicon="{ removeCallback, item }">
          <Button
            @click.stop="removeCallback($event, item)"
            v-bind="Preset.removeTokenIcon"
            icon="close"
            rounded
            severity="dark"
            text
          />
        </template>

        <template #loadingicon>
          <Icon v-bind="Preset.loadingicon" />
        </template>
      </MultiSelect>

      <slot
        :disabled="props.disabled"
        :hide="hideOverlay"
        :show="showOverlay"
        :visible="isShowOverlay"
        name="trigger"
      >
        <Button
          v-if="controls"
          :disabled="props.disabled"
          @click="showOverlay"
          class="!rounded !ring-0"
          icon="add"
        />
      </slot>
    </InputGroup>

    <ValidatorMessage
      :id="props.label + 'error'"
      :message="field.errorMessage"
    />
  </FieldWrapper>
</template>
